import { forwardRef, memo, useCallback, useMemo, useRef, useState } from "react";
import type { ForwardedRef, ReactElement, ReactNode } from "react";
import type { SxProps, TableContainerProps as MuiTableContainerProps, Theme, PaperProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import MuiTableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useVirtualizer } from "@tanstack/react-virtual";
import NoDataWrapper from "components/NoDataWrapper";
import useMergeProps from "hooks/useMergeProps";
import DataTableRow from "./components/DataTableRow";
import DataTableHeadRow from "./components/DataTableHeadRow";
import VirtualSpacerRow from "./components/VirtualSpacerRow";

const TableRoot = styled("div")(() => ({
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
}));

const StyledTableContainer = styled(MuiTableContainer)(({ theme }) => ({
  minHeight: 0,
  overflow: "auto",
  overflowY: "auto",
  paddingBottom: theme.spacing(2),
  overscrollBehavior: "none",
  borderRadius: 0,
  borderBottomRightRadius: theme.shape.borderRadiusLarge,
  borderBottomLeftRadius: theme.shape.borderRadiusLarge,
}));

interface TableContainerProps extends MuiTableContainerProps {
  elevation?: PaperProps["elevation"];
}

const TableContainer = memo(
  forwardRef<HTMLDivElement, MuiTableContainerProps>((props, ref) => (
    <StyledTableContainer component={Paper} ref={ref} {...props} />
  ))
);

export type SortDirection = "asc" | "desc";

export interface SortState<T> {
  columnId: keyof T;
  direction: SortDirection;
}

/**
 * Distributive mapped type — `cell` for each column is narrowed to that
 * column's value type at the definition site.
 */
export type DataTableColumn<T> = {
  [K in keyof T]: {
    /** null id can be used for columns that don't correspond to a specific field in the data (e.g. action buttons) */
    id: (K & string) | null;
    header?: ReactNode;
    align?: "left" | "right" | "center";
    width?: number | string;
    minWidth?: number;
    maxWidth?: number;
    /**
     * Default sort direction when clicking on the column header for the first time. Defaults to "asc".
     */
    initialSortDirection?: SortDirection;
    /**
     * Optional tooltip for the column header, shown when hovering over the header if the column is sortable.
     * Useful for describing the sorting behavior when it's not obvious from the column header alone (e.g. "Sorts by date created, not last updated").
     */
    sortTitle?: (direction: SortDirection) => ReactNode;
    /**
     * Custom comparator for this column. Providing it enables sorting on the column;
     * without it the column is not sortable.
     */
    sortComparator?: (a: T, b: T) => number;
    /**
     * Optional grouping function applied before `sortComparator`. Rows with a lower
     * group value sort first, and the group order is NOT reversed when the column
     * sort direction flips — `sortComparator` only orders rows within the same group.
     */
    sortGroupBy?: (row: T) => number;
    /** Per-column cell renderer. Falls back to the raw value when omitted. */
    cell?: (value: T[K], row: T, index: number) => ReactNode;
    /**
     * Per-cell click handler, useful when clicking a cell should trigger a different action than clicking the row.
     *
     * Propagation is NOT stopped by default: if both the row and the cell have click handlers,
     * the row handler also fires on a cell click. Call `event.stopPropagation()` here to prevent that.
     */
    onCellClick?: (event: React.MouseEvent<HTMLTableCellElement>, value: T[K], row: T, index: number) => void;
    /**
     * Optional function to apply class names to cells in this column,
     * useful for conditional styling (e.g. highlighting cells based on their value).
     */
    className?: (row: T) => string | undefined;
  };
}[keyof T];

type BodyRowSlotProps = Omit<React.ComponentProps<typeof TableRow>, "ref">;
type BodyCellSlotProps = React.ComponentProps<typeof TableCell>;

export interface DataTableSlotProps {
  root?: Omit<React.ComponentProps<typeof TableRoot>, "ref">;
  container?: TableContainerProps;
  table?: Omit<React.ComponentProps<typeof Table>, "size"> & { sx?: SxProps<Theme> };
  headRow?: React.ComponentProps<typeof TableRow>;
  headCell?: React.ComponentProps<typeof TableCell>;
  bodyRow?: BodyRowSlotProps;
  bodyCell?: BodyCellSlotProps;
}

interface VirtualizedDataTableProps<T> {
  data: T[];
  columns: readonly DataTableColumn<T>[];
  keyExtractor: (row: T, index: number) => string | number;
  /** Estimated row height in pixels. Used by the virtualizer to size the scroll area. */
  estimateSize: (index: number) => number;
  /**
   * The number of extra rows rendered above and below the visible area for smoother scrolling.
   * @default 20
   */
  overscan?: number;
  size?: "small" | "medium";
  theme?: "light" | "dark";
  /** Default sort for uncontrolled mode. */
  defaultSort?: SortState<T> | null;
  /**
   * If true, the table header will stick to the top of the scroll container when scrolling.
   * @default true
   */
  stickyHeader?: boolean;
  /**
   * Hides the header row visually while keeping the column headers available to screen readers (WCAG 1.3.1).
   * Sorting from the header and the sticky header are disabled.
   * @default false
   */
  visuallyHiddenHeader?: boolean;
  TableEmptyComponent?: React.ReactNode;
  /**
   * Mouse-only: rows are not focusable and do not respond to the keyboard.
   * Always pair it with a link or button inside a cell that performs the same action,
   * and stop that element's click propagation to avoid firing both.
   */
  onRowClick?: (row: T, rowIndex: number) => void;
  /**
   * Optional function to apply class names to rows,
   * useful for conditional styling (e.g. highlighting rows based on their data).
   */
  className?: (row: T) => string | undefined;
  slotProps?: DataTableSlotProps;
}

const DEFAULT_PROPS = {
  stickyHeader: true,
  visuallyHiddenHeader: false,
  size: "medium" as "small" | "medium",
  overscan: 20,
  theme: "light" as "light" | "dark",
} as const;

function VirtualizedDataTableInner<T>(props: VirtualizedDataTableProps<T>, ref: ForwardedRef<HTMLDivElement | null>) {
  // No need to deep clone data, columns and TableEmptyComponent since they are not mutated and only used for rendering
  const { data, columns, TableEmptyComponent, estimateSize, onRowClick, className, keyExtractor, ...rest } = props;
  const { defaultSort, slotProps, stickyHeader, visuallyHiddenHeader, size, overscan, theme } = useMergeProps(
    DEFAULT_PROPS,
    rest
  );

  const [internalSort, setInternalSort] = useState<SortState<T> | null>(defaultSort ?? null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleSort = useCallback(
    (columnId: keyof T) => {
      const initialDirection = columns.find((c) => c.id === columnId)?.initialSortDirection ?? "asc";
      const oppositeDirection = initialDirection === "asc" ? "desc" : "asc";

      // different column -> sort using the column's initial direction
      if (internalSort?.columnId !== columnId) {
        setInternalSort({ columnId, direction: initialDirection });
      } else if (internalSort.direction === initialDirection) {
        // same column & initial direction -> flip to the opposite
        setInternalSort({ columnId, direction: oppositeDirection });
      } else {
        // same column & opposite direction -> remove sort
        setInternalSort(null);
      }
    },
    [internalSort, columns]
  );

  const sortedData = useMemo(() => {
    if (!internalSort || data.length < 2) return data;

    const column = columns.find((c) => c.id === internalSort.columnId);
    if (!column?.sortComparator) return data;

    const { direction } = internalSort;
    const baseCompare = column.sortComparator;

    const innerCompare = direction === "asc" ? baseCompare : (a: T, b: T) => baseCompare(b, a);

    const sortGroupBy = column.sortGroupBy;
    const compare: (a: T, b: T) => number = sortGroupBy
      ? (a, b) => {
          const groupDiff = sortGroupBy(a) - sortGroupBy(b);
          return groupDiff !== 0 ? groupDiff : innerCompare(a, b);
        }
      : innerCompare;

    return data.slice().sort(compare);
  }, [internalSort, data, columns]);

  const hasData = useMemo(() => sortedData.length !== 0, [sortedData.length]);

  const headRowSlotProps = useMemo(
    () => ({ headRow: slotProps?.headRow, headCell: slotProps?.headCell }),
    [slotProps?.headRow, slotProps?.headCell]
  );
  const bodyRowSlotProps = useMemo(
    () => ({ bodyRow: slotProps?.bodyRow, bodyCell: slotProps?.bodyCell }),
    [slotProps?.bodyRow, slotProps?.bodyCell]
  );

  const rowVirtualizer = useVirtualizer({
    count: sortedData.length,
    getScrollElement: () => scrollRef.current,
    estimateSize,
    overscan,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const headerRowCount = 1;
  const totalSize = rowVirtualizer.getTotalSize();

  /**
   * To create the illusion of a fully rendered table, we add padding elements
   * (VirtualSpacerRow) above and below the rendered rows to take up the remaining space.
   * The height of these spacers is calculated based on the position of the first and last
   * rendered rows relative to the total size of all rows. This way, as the user scrolls,
   * it appears as though all rows are present, while in reality only a subset is rendered at
   * any given time.
   */
  const spacer = useMemo(() => {
    const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
    const paddingBottom = virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0;
    return { paddingTop, paddingBottom };
  }, [virtualRows, totalSize]);

  return (
    <NoDataWrapper hasData={hasData} fallback={TableEmptyComponent}>
      <TableRoot ref={ref} {...slotProps?.root} data-theme={theme}>
        <TableContainer ref={scrollRef} {...slotProps?.container} data-theme={theme}>
          <Table
            stickyHeader={stickyHeader && !visuallyHiddenHeader}
            size={size}
            aria-rowcount={headerRowCount + sortedData.length}
            {...slotProps?.table}
            data-theme={theme}
          >
            <TableHead>
              <DataTableHeadRow
                tableRowIndex={0}
                columns={columns}
                sort={internalSort}
                size={size}
                onSort={handleSort}
                visuallyHidden={visuallyHiddenHeader}
                slotProps={headRowSlotProps}
                theme={theme}
              />
            </TableHead>
            <TableBody sx={{ position: "relative" }} data-theme={theme}>
              <VirtualSpacerRow height={spacer.paddingTop} colSpan={columns.length} />
              {virtualRows.map((virtualRow) => {
                const row = sortedData[virtualRow.index];
                return (
                  <DataTableRow
                    key={keyExtractor(row, virtualRow.index)}
                    className={className}
                    onRowClick={onRowClick}
                    row={row}
                    rowIndex={virtualRow.index}
                    tableRowIndex={headerRowCount + virtualRow.index}
                    columns={columns}
                    slotProps={bodyRowSlotProps}
                  />
                );
              })}
              <VirtualSpacerRow height={spacer.paddingBottom} colSpan={columns.length} />
            </TableBody>
          </Table>
        </TableContainer>
      </TableRoot>
    </NoDataWrapper>
  );
}

/**
 * A virtualized table component for efficiently rendering large datasets with customizable columns, sorting, and styling.
 * It renders a Paper component as container and Mui Table components for the table structure, and uses @tanstack/react-virtual for virtualization.
 */
const VirtualizedDataTable = memo(forwardRef(VirtualizedDataTableInner)) as unknown as <T>(
  props: VirtualizedDataTableProps<T> & { ref?: ForwardedRef<HTMLDivElement> }
) => ReactElement;

export default VirtualizedDataTable;
